/* global window */
'use strict';

const performanceMeasure = require('../lib/performanceMeasure.js');
let measure = performanceMeasure('bootscripts:PhoenixGoogleAnalytics');
const actionMapping = require('../lib/GA4Tracking/actionMapping.json');

// Given an analytics model and a mapping from model keys to GA custom dimension indices, return a
// mapping from GA custom dimension indices to the corresponding values.
function mapDimensions(model, mapping) {
    if (mapping == null) {
        return undefined;
    }
    const customDimensions = {};
    for (const key in model) {
        if (!Object.prototype.hasOwnProperty.call(model, key)) {
            continue;
        }
        const dimensionIdx = mapping[key];
        const value = model[key];
        if (typeof dimensionIdx !== 'number') {
            continue;
        }
        if (value == null || value === '') {
            continue;
        }

        customDimensions['dimension' + dimensionIdx] = String(value);
    }
    return customDimensions;
}
class PhoenixGoogleAnalyticsBase {
    constructor(config) {
        this.config = Object.assign(
            {
                trackers: [],
                extraModelData: {
                    channelName: 'Web',
                    positionInSession: 'subsequent',
                },
            },
            config
        );
    }

    /**
     * Send a GA page view to all configured trackers.
     */
    sendPageView(url, item) {
        // Pageview tracking is special because we track our initial pageview
        // in some code directly inside the page.pug template and then
        // subsequent pageviews in here.

        var baseModel = (item && item.viewProperties && item.viewProperties.analyticsModel) || {};
        var model = Object.assign({}, baseModel, this.config.extraModelData);
        model.path = url;
        this.config.sendPageView(model, this.config.type);
    }
}

/**
 * Iterates through the regexMap to attempt to define a GA4 action for `label`. The keys of regexMap
 * are required to be regex patterns. The values of regexMap are required to be GA4 actions.
 * @param regexMap - A pattern to GA4 action map.
 * @param label - The event label.
 * @returns {string|undefined}
 */
function regexToAction(regexMap, label) {
    try {
        for (const [labelPattern, action] of Object.entries(regexMap)) {
            const regex = new RegExp(labelPattern);
            if (regex.test(label)) {
                return action;
            }
        }
        return undefined;
    } catch (e) {
        return undefined;
    }
}

function getGa4Action(category, action, label) {
    let actionGA4;
    switch (category) {
        case 'video':
        case 'Video':
            actionGA4 = actionMapping['video'][action];
            break;

        case 'Video Mute/Unmute':
            actionGA4 = actionMapping['Video Mute/Unmute'][label];
            break;

        case 'Page Interaction':
            const regexMapping = actionMapping['pageInteraction']['regexMap'];
            actionGA4 = actionMapping['pageInteraction'][label] || regexToAction(regexMapping, label);
            break;
        case 'Outbrain':
            actionGA4 = action;
            break;
        default:
            actionGA4 = undefined;
    }
    return actionGA4;
}

class PhoenixGoogleAnalytics extends PhoenixGoogleAnalyticsBase {
    constructor(config) {
        super(config);
    }

    /**
     * Send a GA event to all configured trackers.
     */
    sendEvent(category, action, label, value, customMap = {}) {
        this.config.trackers.forEach((tracker) => {
            if (!tracker.sendEvents) {
                return;
            }
            const command = PhoenixGoogleAnalytics.setNamedEventType(tracker, 'send');
            window.ga(command, 'event', category, action, label, value, customMap);
        });
    }

    /**
     * Send a GA event with custom dimensions to all configured trackers.
     */
    sendEventCustomDimensions(category, action, label, value, customDimensionsModel) {
        this.config.trackers.forEach((tracker) => {
            if (!tracker.sendEvents) {
                return;
            }
            const customMap = mapDimensions(customDimensionsModel, tracker.customEventDimensionMap);
            const command = PhoenixGoogleAnalytics.setNamedEventType(tracker, 'send');
            window.ga(command, 'event', category, action, label, value, customMap);
        });
    }

    static setNamedEventType(tracker, command) {
        return tracker.name ? `${tracker.name}.${command}` : command;
    }
}

/**
 * Move the analytics.js-style positional parameters
 * to gtag-style parameter object.
 *
 * UA gtag says the parameters should be named
 * event_category (ec query param), event_label (el), and value (ev)
 * (though experimentally, eventCategory and eventLabel also work)
 * https://developers.google.com/analytics/devguides/collection/gtagjs/events
 * and the UA to GA4 migration guide also recommends using
 * event_category (ep.event_category), event_label (ep.event_label), value (epn.value)
 * https://support.google.com/analytics/answer/11091025?sjid=5846676736474613943-NC
 *
 * For nonInteraction, UA gtag requires the parameter to be named
 * non_interaction instead of nonInteraction
 * https://developers.google.com/analytics/devguides/collection/gtagjs/events#send_non-interaction_events
 * GA4 does not treat it specially; it becomes ep.non_interaction=true
 *
 * Note that event action (UA query parameter ea, GA4 query parameter en)
 * does not go into the parameter object.
 * UA gtag supports calling it as gtag('event', 'send', {event_action}),
 * but gtag GA4 requires the action to be a positional parameter.
 */
function moveToParams(category, label, value, customMap) {
    const params = {
        event_category: category,
        event_label: label,
        value,
        // If the parameter in the map is the 'nonInteraction'
        // parameter, convert it to 'non_interaction' so it
        // can be passed into GTAG. Else, just add the parameter
        // and value to the parameters
        non_interaction: customMap?.nonInteraction ?? customMap?.non_interaction,
        ...customMap,
    };
    delete params.nonInteraction;
    return params;
}

class PhoenixGoogleTagAnalytics extends PhoenixGoogleAnalyticsBase {
    constructor(config) {
        super(config);
    }

    sendEvent(category, action, label, value, customMap = {}) {
        const params = moveToParams(category, label, value, customMap);
        this.config.trackers.forEach((tracker) => {
            if (!tracker.sendEvents) {
                return;
            }
            params.send_to = tracker.id;
            window.gtag('event', action, params);
        });
    }

    sendEventCustomDimensions(category, action, label, value, customDimensionsModel) {
        this.config.trackers.forEach((tracker) => {
            if (!tracker.sendEvents) {
                return;
            }
            const customMap = mapDimensions(customDimensionsModel, tracker.customEventDimensionMap);
            const params = moveToParams(category, label, value, customMap);
            params.send_to = tracker.id;
            window.gtag('event', action, params);
        });
    }
}

class PhoenixGoogleGa4Analytics extends PhoenixGoogleAnalyticsBase {
    constructor(config) {
        super(config);
    }

    sendEvent(category, action, label, value, customMap = {}) {
        action = getGa4Action(category, action, label);
        if (action === undefined) {
            return;
        }
        const trackerIds = this.config.trackers
            .filter((tracker) => tracker.sendEvents)
            .map((tracker) => tracker.id);
        const params = moveToParams(category, label, value, customMap);
        params.send_to = trackerIds;
        if (trackerIds.length > 0) {
            window.gtag('event', action, params);
        }
    }
    /** same as sendEvent */
    sendEventCustomDimensions(category, action, label, value, customDimensionsModel) {
        this.sendEvent(category, action, label, value, customDimensionsModel);
    }
}

// Class for multiple analytics objects
class PhoenixGoogleAnalyticsMulti {
    constructor(analyticsObjects) {
        this.analyticsObjects = analyticsObjects;
    }

    sendEvent(category, action, label, value, customMap) {
        this.analyticsObjects.forEach((analytics) =>
            analytics.sendEvent(category, action, label, value, customMap)
        );
    }

    sendEventCustomDimensions(category, action, label, value, customDimensionsModel) {
        this.analyticsObjects.forEach((analytics) =>
            analytics.sendEventCustomDimensions(category, action, label, value, customDimensionsModel)
        );
    }

    sendPageView(url, item) {
        this.analyticsObjects.forEach((analytics) => analytics.sendPageView(url, item));
    }
}

measure.end();

window.googleAnalyticsConfigPromise.then(() => {
    const type = window.googleAnalyticsConfig.type;
    const config = window.googleAnalyticsConfig;
    const trackersUa = [];
    const trackersGa4 = [];
    config.trackers.forEach((tracker) => {
        if (config.isGA4Tracker({ id: tracker.id, name: tracker.name })) {
            trackersGa4.push(tracker);
        } else {
            trackersUa.push(tracker);
        }
    });
    switch (type) {
        case 'ga-ga4': // Fire both ga and ga4 events
            window.googleAnalytics = new PhoenixGoogleAnalyticsMulti([
                new PhoenixGoogleGa4Analytics({ ...config, trackers: trackersGa4, type: 'ga4' }),
                new PhoenixGoogleAnalytics({ ...config, trackers: trackersUa, type: 'ga' }),
            ]);
            break;
        case 'ga4': // Fire ga4 events
            window.googleAnalytics = new PhoenixGoogleGa4Analytics({ ...config, trackers: trackersGa4 });
            break;
        case 'ga4-gtag': // Fire both ga4 and gtag events
        default:
            window.googleAnalytics = new PhoenixGoogleAnalyticsMulti([
                new PhoenixGoogleTagAnalytics({ ...config, trackers: trackersUa, type: 'gtag' }),
                new PhoenixGoogleGa4Analytics({ ...config, trackers: trackersGa4, type: 'ga4' }),
            ]);
            break;
    }
    window.googleAnalyticsPromiseResolved();
});
